<template>
  <b-card>
    <b-card-body>
      <b-form @submit.prevent="onSubmit">
        <b-row class="flex-column align-items-center">
          <b-col cols="12" md="8" lg="6" class="mb-2">
            <b-form-group style="color: #d0d2d6" :label="t('Select Customer')">
              <v-select
                v-model="form.customer_id"
                :options="customersOptions"
                :placeholder="t('Make a Choice')"
                label="text"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="8" lg="6" class="mb-2">
            <b-form-group style="color: #d0d2d6" :label="t('Acceptance Start Date')">
              <b-input-group class="input-group-merge">
                <flat-pickr
                  v-model="form.from_date"
                  :locale="'tr'"
                  class="form-control"
                  :placeholder="t('Select Date')"
                  :config="startPickerConfig"
                />
                <b-input-group-append is-text>
                  <feather-icon icon="XIcon" class="cursor-pointer" @click="form.from_date = ''" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="8" lg="6" class="mb-2">
            <b-form-group style="color: #d0d2d6" :label="t('Acceptance End Date')">
              <b-input-group class="input-group-merge">
                <flat-pickr
                  v-model="form.to_date"
                  :locale="'tr'"
                  class="form-control"
                  :placeholder="t('Select Date')"
                  :config="endPickerConfig"
                />
                <b-input-group-append is-text>
                  <feather-icon icon="XIcon" class="cursor-pointer" @click="form.to_date = ''" />
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="8" lg="6" class="mb-3">
            <b-form-group style="color: #d0d2d6" :label="t('Report Type')">
              <v-select
                v-model="form.report_type"
                :options="reportTypeOptions"
                :placeholder="t('Make a Choice')"
                label="text"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="8" lg="6">
            <b-button :disabled="submitting" type="submit" variant="primary" block>
              {{ t('Download Report') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardBody,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BInputGroup,
  BInputGroupAppend
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import flatPickr from 'vue-flatpickr-component';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import useService from '@/auth/service/useService';

export default {
  name: 'CreateReport',
  components: {
    BCard,
    BCardBody,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BInputGroup,
    BInputGroupAppend,
    vSelect,
    flatPickr
  },
  mixins: [helper],
  data() {
    return {
      submitting: false,
      customers: [],
      dateTimePickerConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        time_24hr: true
      },
      form: {
        customer_id: '',
        from_date: '',
        to_date: '',
        report_type: ''
      }
    };
  },
  computed: {
    ...mapGetters({}),
    customersOptions() {
      // 'this.customers' zaten { text, value } yapısında set ediliyor
      return this.customers || [];
    },
    reportTypeOptions() {
      return [
        { text: this.$t('Shipment Invoice Detail Report'), value: 'consignment-invoice-detail' }
      ];
    },
    startPickerConfig() {
      return {
        ...this.dateTimePickerConfig,
        // Bitiş tarihi seçiliyse, başlangıç için üst sınır
        maxDate: this.form.to_date || null
      };
    },
    endPickerConfig() {
      return {
        ...this.dateTimePickerConfig,
        // Başlangıç tarihi seçiliyse, bitiş için alt sınır
        minDate: this.form.from_date || null,
        position: 'below'
      };
    }
  },
  async created() {
    try {
      const customersResponse = await useService
        .getAllCustomers({ paginated: false })
        .catch(() => ({ data: [] }));

      this.customers = (customersResponse?.data || []).map(c => ({
        text: c.name,
        value: c.customerCode
      }));
    } catch (e) {
      this.customers = [];
    }
  },
  methods: {
    parseDateString(value) {
      if (!value) return null;
      // Flatpickr formatı: 'YYYY-MM-DD HH:mm' → ISO'ya çevirip güvenli parse
      const isoLike = typeof value === 'string' ? value.replace(' ', 'T') : value;
      const dateObj = new Date(isoLike);
      return Number.isNaN(dateObj.getTime()) ? null : dateObj;
    },
    async onSubmit() {
      if (!this.form.customer_id) return this.showErrorMessage(this.$t('Please select a customer'));
      if (!this.form.from_date) return this.showErrorMessage(this.$t('Please select a start date'));
      if (!this.form.to_date) return this.showErrorMessage(this.$t('Please select an end date'));
      if (!this.form.report_type)
        return this.showErrorMessage(this.$t('Please select report type'));

      // Tarih aralığı doğrulaması
      const fromDate = this.parseDateString(this.form.from_date);
      const toDate = this.parseDateString(this.form.to_date);
      if (fromDate && toDate && fromDate > toDate) {
        return this.showErrorMessage(this.$t('Start date must be before end date'));
      }

      const requestData = {
        export_format: 'application/excel',
        customerCode: this.form.customer_id && this.form.customer_id.value,
        created_at: {
          start: this.form.from_date,
          end: this.form.to_date
        }
      };

      this.submitting = true;
      try {
        const res = await useService.adminExportConsignmentReport(requestData);
        this.showSuccessMessage(res.message || this.$t('Download successful'));
      } catch (err) {
        this.showErrorMessage(this.$t('Download failed'));
      } finally {
        this.submitting = false;
      }
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
